import React, { ReactElement } from "react";
import styles from "./OfferSEOHeader.module.css";
import { USPAttributesGroup } from "@/types/sites";
import type {
    OfferPageTemplateResponse,
    OfferHeaderAttributions,
} from "@/types/offers";
import { CmsImage } from "@/components/common/CmsImage";
import { USPAttributesIconGroup } from "@/components/common/USPAttributesIconGroup";
import { OfferHeaderAttribution } from "@/components/OfferPage/OfferHeaderAttribution";
import {
    dateOrNull,
    formatDate,
    getCurrentDate,
    getDateFormatFromCMSEnumeration,
    getMostRecentDate,
} from "../../../utils/timeUtils";
import { enableBrTags, replaceDatePlaceholders } from "../../../utils/titleUtils";
import { useBreakpoint } from "@/hooks/useBreakpoint";

const AttributionComponent = ({
    offer_attributions,
    hasImage
}: {
    offer_attributions: OfferHeaderAttributions[] | undefined;
    hasImage: boolean;
}): ReactElement | null => {
    return offer_attributions && offer_attributions.length > 0 ? (
        <div className={[styles.attributions, styles[hasImage ? "justifyLeft" : ""]].join(" ")}>
            {offer_attributions.map((attribution) => {
                const { attribution_title, attribution_sub_title, attribution_image } = attribution;
                if (attribution_image || attribution_title || attribution_sub_title) {
                    return (
                        <OfferHeaderAttribution
                            key={attribution.id}
                            attributionTitle={attribution_title}
                            attributionSubTitle={attribution_sub_title}
                            logo={attribution.attribution_image}
                        />
                    );
                }
                return null;
            })}
        </div>
    ) : null;
};

export const OfferSEOHeader = ({
    offerPage,
    pageUSPAttributes,
    initialViewportWidth
}: {
    offerPage: OfferPageTemplateResponse;
    pageUSPAttributes?: USPAttributesGroup | null;
    initialViewportWidth: number;
}): React.ReactElement => {
    const mobileBreakpoint = offerPage?.data?.attributes?.OP?.mobileBreakpoint ?? 768;
    const { isMobileWidth } = useBreakpoint(mobileBreakpoint, initialViewportWidth);

    const hasSubtitle =
        offerPage?.data?.attributes?.subtitle &&
        offerPage?.data?.attributes?.subtitle.trim() != "";

    const hasOwnSubtitle =
        offerPage?.data?.attributes?.offer_header?.data?.attributes?.subtitle &&
        offerPage?.data?.attributes?.offer_header?.data?.attributes?.subtitle.trim() !=
            "";

    const offer_attributions_start =
        offerPage.data?.attributes?.offer_header?.data?.attributes
            .offer_start_attribution || undefined;
    const offer_attributions_end =
        offerPage.data?.attributes?.offer_header?.data?.attributes
            .offer_end_attribution || undefined;
    const hasImage = !!offerPage?.data?.attributes?.offer_header?.data?.attributes
        ?.header_image?.data;

    const offerPageLastUpdated = dateOrNull(
        offerPage.data?.attributes.updatedAt
    );
    const offerListLastUpdated = dateOrNull(
        offerPage.data?.attributes?.offer_list?.data?.attributes?.updatedAt
    );

    const mostRecentDateOrNull = getMostRecentDate([
        offerListLastUpdated,
        offerPageLastUpdated,
    ]);

    const dateFormat = getDateFormatFromCMSEnumeration(
        offerPage?.data?.attributes?.offer_header?.data?.attributes?.dateFormat
    );

    const datePrefix = offerPage?.data?.attributes?.offer_header?.data
        ?.attributes?.datePrefix
        ? offerPage?.data?.attributes?.offer_header?.data?.attributes
              ?.datePrefix
        : "Updated: ";

    let formattedDate = "";
    if (dateFormat) {
        formattedDate = mostRecentDateOrNull
            ? formatDate(mostRecentDateOrNull, dateFormat)
            : getCurrentDate(dateFormat);
    }

    const textAlignStyle =
        styles[hasImage ? "textAlignLeft" : "textAlignCenter"];

    const headerStyles = offerPage.data?.attributes?.offer_header?.data?.attributes?.styles;
    const backgroundColor = offerPage.data?.attributes?.offer_header?.data?.attributes?.background_color;
    const titleString = offerPage.data?.attributes?.offer_header?.data?.attributes?.title_override || offerPage.data?.attributes?.title;
    const title = replaceDatePlaceholders(titleString || '');
    const attrPosition = offerPage.data?.attributes?.offer_header?.data?.attributes?.attribution_location || "standard";

    const headerImage = offerPage?.data?.attributes?.offer_header?.data?.attributes?.header_image;
    const mobileHeaderImage = offerPage?.data?.attributes?.offer_header?.data?.attributes?.header_image_mobile;
    const imageToUse = isMobileWidth && mobileHeaderImage?.data ? mobileHeaderImage : headerImage;


    return (
        <section id="header" className={styles.header} style={backgroundColor ? {'--header-background-color': backgroundColor} as React.CSSProperties : undefined}>
            <div className={styles.offerHeaderContainer}>
                <div className={styles.offerHeaderContent}>
                    <h1 
                    className={[styles.title, textAlignStyle].join(" ")}
                    dangerouslySetInnerHTML={{__html: title}}></h1>
                   { attrPosition == "standard" && (<AttributionComponent 
                        offer_attributions={offer_attributions_start}
                        hasImage={hasImage}
                    />)}
                    {hasSubtitle || hasOwnSubtitle ? (
                        <h2
                            className={[styles.subtitle, textAlignStyle].join(
                                " "
                            )}
                        >
                            {enableBrTags(
                                hasOwnSubtitle
                                    ? offerPage?.data?.attributes?.offer_header
                                          ?.data?.attributes?.subtitle
                                    : offerPage?.data?.attributes?.subtitle
                            )}
                        </h2>
                    ) : null}
                    {formattedDate && (
                        <h2
                            className={[
                                styles.updatedDate,
                                textAlignStyle,
                            ].join(" ")}
                        >
                            {datePrefix}
                            {formattedDate}
                        </h2>
                    )}
                    { attrPosition == "standard" && (<AttributionComponent 
                        offer_attributions={offer_attributions_end}
                        hasImage={hasImage}
                    />)}
                </div>
                {imageToUse?.data && (
                    <div className={styles.offerHeaderImage}>
                        <CmsImage
                            cmsMedia={imageToUse}
                            desiredHeight={300}
                            responsive={true}
                            loading="eager"
                        />
                    </div>
                )}
                                { attrPosition == "below" && (
                <div className={styles.offerAttribution}>
                    <AttributionComponent 
                        offer_attributions={offer_attributions_start}
                        hasImage={hasImage}
                    />
                    <AttributionComponent 
                        offer_attributions={offer_attributions_end}
                        hasImage={hasImage}
                    />
                </div>)}
            </div>

            {pageUSPAttributes && (
                <USPAttributesIconGroup pageUSPAttributes={pageUSPAttributes} />
            )}
            { headerStyles && <style>{headerStyles}</style> }
        </section>
    );
};
